import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io, type Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "util/UserContext";
import AuthService from "services/authService";
import { chatQueryKeys } from "hooks/queries/useChatQueries";
import type { ChatUnreadCounts } from "types/chat";
import { useNotificationBroadcast } from "hooks/useNotificationBroadcast";

/**
 * Query key factory for notification TanStack Query cache. Duplicated from
 * useNotificationQueries to avoid a circular dependency since the query hooks
 * import from this provider.
 */
const notificationQueryKeys = {
  all: () => ["notifications"] as const,
  unreadCount: () => ["notifications", "unreadCount"] as const,
  list: () => ["notifications", "list"] as const,
};

interface ChatSocketEventPayload {
  conversationPublicId?: string;
  message?: {
    public_id?: string;
    thread_root_message_public_id?: string | null;
  };
}

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  unreadCounts: ChatUnreadCounts | null;
  joinConversation: (conversationPublicId: string) => void;
  emitTypingStart: (conversationPublicId: string) => void;
  emitTypingStop: (conversationPublicId: string) => void;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

const SOCKET_EVENT_NAMES = {
  MESSAGE: "chat:message",
  MESSAGE_EDIT: "chat:message:edit",
  MESSAGE_DELETE: "chat:message:delete",
  REACTION: "chat:reaction",
  REQUEST: "chat:request",
  CONVERSATION_UPDATE: "chat:conversation:update",
  UNREAD: "chat:unread",
  JOIN: "chat:join",
  TYPING_START: "chat:typing:start",
  TYPING_STOP: "chat:typing:stop",
  PRESENCE_HEARTBEAT: "chat:presence:heartbeat",
  NOTIFICATION_NEW: "notification:new",
  NOTIFICATION_COUNT: "notification:count",
  NOTIFICATION_READ: "notification:read",
} as const;

/**
 * Minimum interval between forced reconnects when the access token
 * rotates. Prevents a brief refresh storm from triggering multiple
 * disconnect/connect pairs in flight.
 */
const TOKEN_RECONNECT_DEBOUNCE_MS = 2000;

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const heartbeatRef = useRef<number | null>(null);
  const lastTokenReconnectAtRef = useRef<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [unreadCounts, setUnreadCounts] = useState<ChatUnreadCounts | null>(null);
  const shouldConnect = Boolean(currentUser);

  useNotificationBroadcast();

  const invalidateConversationPayload = useCallback(async (payload?: ChatSocketEventPayload) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] }),
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.requests() }),
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.unread() }),
    ]);

    if (payload?.conversationPublicId) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.conversation(payload.conversationPublicId) }),
        queryClient.invalidateQueries({ queryKey: chatQueryKeys.messages(payload.conversationPublicId) }),
      ]);
    }

    const threadRootMessagePublicId = payload?.message?.thread_root_message_public_id;
    if (threadRootMessagePublicId) {
      await queryClient.invalidateQueries({ queryKey: chatQueryKeys.thread(threadRootMessagePublicId) });
    }
  }, [queryClient]);

  useEffect(() => {
    if (!shouldConnect) {
      if (heartbeatRef.current !== null) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      socketRef.current?.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setUnreadCounts(null);
      return;
    }

    const token = AuthService.getAccessToken();
    if (!token) {
      return;
    }

    const socket = io(process.env.REACT_APP_API_URL || "/", {
      transports: ["websocket", "polling"],
      withCredentials: true,
      auth: {
        token,
      },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      socket.emit(SOCKET_EVENT_NAMES.JOIN, { conversationIds: [] });
      if (heartbeatRef.current !== null) {
        window.clearInterval(heartbeatRef.current);
      }
      heartbeatRef.current = window.setInterval(() => {
        socket.emit(SOCKET_EVENT_NAMES.PRESENCE_HEARTBEAT);
      }, 30_000);
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
      if (heartbeatRef.current !== null) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    });

    socket.on(SOCKET_EVENT_NAMES.MESSAGE, (payload: ChatSocketEventPayload) => {
      void invalidateConversationPayload(payload);
    });
    socket.on(SOCKET_EVENT_NAMES.MESSAGE_EDIT, (payload: ChatSocketEventPayload) => {
      void invalidateConversationPayload(payload);
    });
    socket.on(SOCKET_EVENT_NAMES.MESSAGE_DELETE, (payload: ChatSocketEventPayload) => {
      void invalidateConversationPayload(payload);
    });
    socket.on(SOCKET_EVENT_NAMES.REACTION, (payload: ChatSocketEventPayload) => {
      void invalidateConversationPayload(payload);
    });
    socket.on(SOCKET_EVENT_NAMES.REQUEST, (payload: ChatSocketEventPayload) => {
      void invalidateConversationPayload(payload);
    });
    socket.on(SOCKET_EVENT_NAMES.CONVERSATION_UPDATE, (payload: ChatSocketEventPayload) => {
      void invalidateConversationPayload(payload);
    });
    socket.on(SOCKET_EVENT_NAMES.UNREAD, (payload: ChatUnreadCounts) => {
      setUnreadCounts(payload);
      queryClient.setQueryData(chatQueryKeys.unread(), payload);
    });

    socket.on(SOCKET_EVENT_NAMES.NOTIFICATION_NEW, (payload: Record<string, unknown>) => {
      // Chat-related notification types live in the chat surfaces and
      // are excluded from the bell-badge unread count and the
      // notifications modal feed (see backend `notificationModel.js`
      // `NOTIFICATION_FEED_EXCLUDE_CHAT`). Skipping the optimistic +1
      // here keeps the badge stable instead of briefly flashing a
      // higher number until the authoritative `notification:count`
      // event lands.
      const notificationType =
        typeof payload?.notification_type === "string" ? payload.notification_type : "";
      const isChatType = notificationType.startsWith("chat_");

      if (!isChatType) {
        queryClient.setQueryData<{ count: number }>(
          notificationQueryKeys.unreadCount(),
          (prev) => ({ count: (prev?.count ?? 0) + 1 })
        );
        void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list() });
      }

      try {
        const channel = new BroadcastChannel("eoe-notifications");
        channel.postMessage({ type: "notification:new", payload });
        channel.close();
      } catch {
        // BroadcastChannel unavailable in some contexts
      }
    });

    socket.on(SOCKET_EVENT_NAMES.NOTIFICATION_COUNT, (payload: { count: number }) => {
      if (typeof payload?.count === "number") {
        queryClient.setQueryData<{ count: number }>(
          notificationQueryKeys.unreadCount(),
          { count: payload.count }
        );

        try {
          const channel = new BroadcastChannel("eoe-notifications");
          channel.postMessage({ type: "notification:count", count: payload.count });
          channel.close();
        } catch {
          // BroadcastChannel unavailable in some contexts
        }
      }
    });

    socket.on(SOCKET_EVENT_NAMES.NOTIFICATION_READ, () => {
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount() });
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list() });
    });

    /**
     * Reconnect with the latest access token. The handshake middleware
     * on the server reads `socket.handshake.auth.token` once at
     * connection time, so a refresh-rotated JWT cannot be applied to an
     * already-connected socket without a disconnect/connect pair.
     * Debounced so a rapid sequence of refresh calls does not trigger a
     * reconnect storm.
     */
    const reconnectWithLatestToken = (nextToken: string | null): void => {
      if (!nextToken) {
        return;
      }
      const now = Date.now();
      if (now - lastTokenReconnectAtRef.current < TOKEN_RECONNECT_DEBOUNCE_MS) {
        socket.auth = { token: nextToken };
        return;
      }
      lastTokenReconnectAtRef.current = now;
      socket.auth = { token: nextToken };
      socket.disconnect();
      socket.connect();
    };

    const unsubscribeTokenListener = AuthService.onTokenChange((nextToken) => {
      if (!nextToken) {
        return;
      }
      reconnectWithLatestToken(nextToken);
    });

    /**
     * When the tab becomes visible again, or the network comes back
     * online, nudge the socket to reconnect if it disconnected while
     * the tab was hidden. socket.io-client already retries on its own,
     * but explicitly calling `connect()` shortens the gap between
     * "tab is back" and "real-time events resume".
     */
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible" && !socket.connected) {
        socket.connect();
      }
    };
    const handleOnline = (): void => {
      if (!socket.connected) {
        socket.connect();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      unsubscribeTokenListener();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      socket.disconnect();
      if (heartbeatRef.current !== null) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      setIsConnected(false);
    };
  }, [invalidateConversationPayload, queryClient, shouldConnect]);

  const joinConversation = useCallback((conversationPublicId: string) => {
    const socket = socketRef.current;
    if (!socket || !conversationPublicId.trim()) {
      return;
    }
    socket.emit(SOCKET_EVENT_NAMES.JOIN, {
      conversationId: conversationPublicId,
    });
  }, []);

  const emitTypingStart = useCallback((conversationPublicId: string) => {
    const socket = socketRef.current;
    if (!socket || !conversationPublicId.trim()) {
      return;
    }
    socket.emit(SOCKET_EVENT_NAMES.TYPING_START, {
      conversationId: conversationPublicId,
    });
  }, []);

  const emitTypingStop = useCallback((conversationPublicId: string) => {
    const socket = socketRef.current;
    if (!socket || !conversationPublicId.trim()) {
      return;
    }
    socket.emit(SOCKET_EVENT_NAMES.TYPING_STOP, {
      conversationId: conversationPublicId,
    });
  }, []);

  const value = useMemo<SocketContextValue>(() => ({
    socket: socketRef.current,
    isConnected,
    unreadCounts,
    joinConversation,
    emitTypingStart,
    emitTypingStop,
  }), [emitTypingStart, emitTypingStop, isConnected, joinConversation, unreadCounts]);

  return React.createElement(SocketContext.Provider, { value }, children);
};

export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
